import React, { useCallback, useEffect, useState } from "react";
import SectionCard from "components/SectionCard";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import { openDoc } from "lib/docs";
import {
  ISSUE_DOCUMENT_TYPES,
  canDeleteIssueDocument,
  deleteIssueDocument,
  fetchIssueDocuments,
  issueDocumentTypeLabel,
  uploadIssueDocument,
} from "lib/issueDocuments";

export default function IssueDocuments({ deal, canUpload }) {
  const { profile, selectedOrg } = useProfile();
  const { alert, confirm } = useDialog();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("official_statement");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  const refresh = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    try {
      setDocuments(await fetchIssueDocuments(deal.id));
    } catch (error) {
      await alert(error.message || "Unable to load issue documents.", { title: "Load failed" });
    } finally {
      setLoading(false);
    }
  }, [deal?.id, alert]);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!title.trim() || !file) {
      await alert("Enter a title and select a file.", { title: "Document required" });
      return;
    }
    setBusy(true);
    try {
      await uploadIssueDocument({
        dealId: deal.id,
        tenantId: deal.tenantId ?? deal.tenant_id ?? selectedOrg?.tenant_id,
        title: title.trim(), docType, description: description.trim(), file,
      });
      setTitle(""); setDescription(""); setFile(null); setShowUpload(false);
      await refresh();
    } catch (error) {
      await alert(error.message || "Unable to upload the document.", { title: "Upload failed" });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (document) => {
    const approved = await confirm(`Delete “${document.title}”?`, {
      title: "Delete document", okLabel: "Delete", cancelLabel: "Cancel",
    });
    if (!approved) return;
    setBusy(true);
    try {
      await deleteIssueDocument(document);
      setDocuments((current) => current.filter((item) => item.id !== document.id));
    } catch (error) {
      await alert(error.message || "Unable to delete the document.", { title: "Delete failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Issue Documents"
        subtitle="Official statements and other records associated with this issue."
        headerAction={canUpload ? (
          <button type="button" className="btn btn-primary" onClick={() => setShowUpload((value) => !value)}>
            {showUpload ? "Cancel" : "Upload Document"}
          </button>
        ) : null}
      >
        {showUpload ? (
          <form className="mb-5 grid gap-3 rounded-lg border border-border bg-app p-4 md:grid-cols-2" onSubmit={handleUpload}>
            <label className="text-sm">Title
              <input className="input-app mt-1 w-full" value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
            </label>
            <label className="text-sm">Document Type
              <select className="input-app mt-1 w-full" value={docType} onChange={(e) => setDocType(e.target.value)} disabled={busy}>
                {ISSUE_DOCUMENT_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="text-sm md:col-span-2">Description
              <textarea className="input-app mt-1 w-full" value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} />
            </label>
            <label className="text-sm md:col-span-2">File
              <input className="mt-1 block w-full" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} />
            </label>
            <div className="md:col-span-2"><button className="btn btn-primary" type="submit" disabled={busy}>{busy ? "Uploading…" : "Upload"}</button></div>
          </form>
        ) : null}

        {loading ? <div className="text-sm text-muted">Loading documents…</div> : documents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted">No documents are associated with this issue.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left"><th className="p-2">Title</th><th className="p-2">Type</th><th className="p-2">Added</th><th className="p-2 text-right">Actions</th></tr></thead>
              <tbody>{documents.map((document) => (
                <tr key={document.id} className="border-b border-border last:border-0">
                  <td className="p-2"><div className="font-medium">{document.title}</div>{document.description ? <div className="text-xs text-muted">{document.description}</div> : null}</td>
                  <td className="p-2">{issueDocumentTypeLabel(document.doc_type)}</td>
                  <td className="p-2">{new Date(document.created_at).toLocaleDateString()}</td>
                  <td className="p-2 text-right"><button className="btn btn-ghost" type="button" onClick={() => openDoc(document.file_path)}>Open</button>{canDeleteIssueDocument({ document, deal, profile }) ? <button className="btn btn-ghost text-red-600" type="button" disabled={busy} onClick={() => handleDelete(document)}>Delete</button> : null}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
